import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Row,
  Col,
  Button,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete, canView } from "../common/permissionUtils";
import {
  getOrderViewDetailsApi,
  getHistoryApi,
  getWorkflowApi,
  getOrderAttachmentsApi,
  removeOrderAttachmentApi,
  updateOrderStatusApi,
  getOrderInvoicesApi,
} from "./order-api";
import Back from "../../assets/svg/back.svg";
import Search from "../../assets/svg/search.svg";
import ProfileIc from "../../assets/png/profile-ic.png";
import Location from "../../assets/svg/location.svg";
import User from "../../assets/svg/user.svg";
import Mail from "../../assets/svg/mail.svg";
import Phone from "../../assets/svg/phone.svg";
import { ReactComponent as NoteHist } from "../../assets/svg/note_hist.svg";
import { ReactComponent as LeadHist } from "../../assets/svg/lead_hist.svg";
import { ReactComponent as AttachHist } from "../../assets/svg/attach_hist.svg";
import { ReactComponent as StatusHist } from "../../assets/svg/status_hist.svg";
import { ReactComponent as QuotHist } from "../../assets/svg/quot_hist.svg";
import { ReactComponent as EditHist } from "../../assets/svg/edit_hist.svg";
import { ReactComponent as ContactHist } from "../../assets/svg/contact_hist.svg";
import { ReactComponent as EmailHist } from "../../assets/svg/email_hist.svg";
import { ReactComponent as CallHist } from "../../assets/svg/call_hist.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import { ReactComponent as EyeIc } from "../../assets/svg/eye_ic.svg";
import {
  FaCheck,
  FaTimes,
  FaUndo,
  FaUndoAlt,
  FaDownload,
  FaEye,
  FaTrash,
  FaInfoCircle,
  FaFileAlt,
  FaThumbsUp,
  FaCogs,
  FaTruck,
  FaBoxOpen,
  FaClock,
} from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import AttachmentDeleteModal from "../common/AttachmentDeleteModal";
import "./order.css";
import AccountType from "../../assets/svg/account_type.svg";
import Amount from "../../assets/svg/amount_ic1.svg";
import { applyStateTaxFlags } from "../common/state-tax";
import { sanitizeInput } from "../common/security-utils";

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table/card overflow. `rows` is an array of [label, value,
// isTotal?] tuples shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span key={i} className={`tt-row${isTotal ? " tt-total" : ""}`}>
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

const OrderView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Order");
  const hasDeletePermission = canDelete(rolePermissionList, "Order");
  const hasInvoiceViewPermission = canView(rolePermissionList, "Invoice");

  const [order, setOrder] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({ total_invoiced: 0, total_paid: 0, total_outstanding: 0 });
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrderView, setIsOrderView] = useState(false); // PDF view toggle
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [workflowList, setWorkflowList] = useState([]);
  const [activeTab, setActiveTab] = useState("Order Details");
  const [attachments, setAttachments] = useState([]);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [attachmentDeleteModal, setAttachmentDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(null);

  // Which status buttons are available from the order's current status.
  const statusTransitions = {
    Created: ["Confirmed", "Cancelled"],
    Confirmed: ["Delivered", "Cancelled"],
  };

  // Each workflow status gets a distinct, meaningful icon + color so the
  // timeline communicates what actually happened at a glance, rather than
  // every "positive" status collapsing into the same generic checkmark.
  const workflowIcons = {
    "order created": <FaFileAlt style={{ color: "#172B4D" }} />,
    "order updated": <EditHist style={{ color: "#172B4D", fill: "#172B4D" }} />,
    confirmed: <FaThumbsUp style={{ color: "#2E7D32" }} />,
    processing: <FaCogs style={{ color: "#3D4AF5" }} />,
    shipped: <FaTruck style={{ color: "#3D4AF5" }} />,
    delivered: <FaBoxOpen style={{ color: "#2E7D32" }} />,
    completed: <FaCheck style={{ color: "#2E7D32" }} />,
    pending: <FaClock style={{ color: "#B8860B" }} />,
    cancelled: <FaTimes style={{ color: "#D32F2F" }} />,
    "partially cancelled": <MdCancel style={{ color: "#D32F2F" }} />,
    returned: <FaUndo style={{ color: "#D32F2F" }} />,
    "partially returned": <FaUndoAlt style={{ color: "#B8860B" }} />,
  };

  const historyIcons = {
    note: <NoteHist />,
    lead: <LeadHist />,
    attachment: <AttachHist />,
    status: <StatusHist />,
    quotation: <QuotHist />,
    edit: <EditHist />,
    contact: <ContactHist />,
    email: <EmailHist />,
    call: <CallHist />,
    order: <QuotHist />,
  };

  const getCleanType = (type) => {
    if (!type) return "";
    return type.trim().toLowerCase().split(" ")[0];
  };

  useEffect(() => {
    if (id) {
      if (activeTab === "Order Details") {
        fetchOrder();
      } else if (activeTab === "Workflow") {
        fetchWorkflow();
      } else if (activeTab === "Log") {
        fetchHistory();
      } else if (activeTab === "Attachments") {
        fetchAttachments();
      } else if (activeTab === "Invoice") {
        fetchInvoices();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab]);

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await getOrderViewDetailsApi(id);
      const data = response?.data ?? response;
      const orderData = data?.order ?? {};
      setOrder(orderData ?? null);
      setProductList(orderData?.line_items || orderData?.items || []);
      if (orderData?.ship_address != null) {
        if (orderData?.ship_address?.state?.value) {
          updateStateTaxFlags(orderData?.ship_address?.state?.value);
        }
      }
    } catch (err) {
      console.error("Failed to fetch order details", err);
      setSnackbarMessage("Failed to fetch order details");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidId = (id) => {
    if (!id) return false;
    return /^[a-zA-Z0-9_-]+$/.test(String(id));
  };

  const updateStatus = async (value) => {
    if (!isValidId(id)) {
      return;
    }

    setLoadingStatus(value);
    try {
      const bodyData = {
        status: sanitizeInput(value),
      };
      const response = await updateOrderStatusApi(bodyData, id);

      setSnackbarMessage(sanitizeInput(response?.data?.message ?? ""));
      setSnackbarStatus(response?.data?.success ?? false);
      setShowSnackbar(true);

      fetchOrder();
    } catch (err) {
      console.error("Failed to update order status", err);
      setSnackbarMessage("Failed to update order status");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setLoadingStatus(null);
    }
  };

  const fetchHistory = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getHistoryApi(id);
      if (res.status === 200) {
        setHistoryList(res.data.histories || []);
      }
    } catch (err) {
      console.error("Failed to fetch order history", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWorkflow = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getWorkflowApi(id);
      if (res.status === 200) {
        setWorkflowList(res.data.workflows || []);
      }
    } catch (err) {
      console.error("Failed to fetch order workflow", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAttachments = async () => {
    if (!isValidId(id)) {
      console.error("Invalid order ID");
      return;
    }

    try {
      const response = await getOrderAttachmentsApi(id);
      const attachmentList = response?.data?.attachments ?? [];

      const sanitizedAttachments = attachmentList.map((att) => ({
        ...att,
        file_name: sanitizeInput(att.file_name ?? ""),
      }));

      setAttachments(sanitizedAttachments);
    } catch (err) {
      console.error("Failed to fetch order attachments", err);
      setSnackbarMessage("Failed to fetch attachments");
      setSnackbarStatus(false);
      setShowSnackbar(true);
    }
  };

  const fetchInvoices = async (search = invoiceSearch) => {
    if (!isValidId(id)) return;
    setIsInvoiceLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await getOrderInvoicesApi(id, params);
      if (response?.data?.success) {
        setInvoiceList(response.data.invoices || []);
        setInvoiceSummary(
          response.data.summary || { total_invoiced: 0, total_paid: 0, total_outstanding: 0 },
        );
      }
    } catch (err) {
      console.error("Failed to fetch order invoices", err);
    } finally {
      setIsInvoiceLoading(false);
    }
  };

  const isImageFile = (fileName) => {
    const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const ext = fileName.split(".").pop().toLowerCase();
    return imageExts.includes(ext);
  };

  const isPdfFile = (fileName) => {
    return fileName.toLowerCase().endsWith(".pdf");
  };

  const isPreviewableFile = (fileName) => {
    return isImageFile(fileName) || isPdfFile(fileName);
  };

  const handleOpenPreview = (attachment) => {
    if (isPreviewableFile(attachment.file_name)) {
      // Ensure file_path is available for URL construction
      const previewAttachment = {
        ...attachment,
        file_path: attachment.file_path || attachment.file_url || "",
      };
      console.log("Opening preview for:", previewAttachment);
      setPreviewData(previewAttachment);
      setPreviewModal(true);
    }
  };

  const handleDownloadAttachment = (attachment) => {
    if (!attachment.file_path && !attachment.file_url) {
      console.error("No file path available for download");
      setSnackbarMessage("⚠ Download failed: No file path available");
      setSnackbarStatus(false);
      setShowSnackbar(true);
      return;
    }

    try {
      // Use file_path if available, fallback to file_url
      let fileUrl = attachment.file_path || attachment.file_url;

      if (!fileUrl) {
        throw new Error("No file path or URL available");
      }

      console.log("Downloading from URL:", fileUrl);
      console.log("Attachment data:", attachment);

      // Check if URL is absolute (http/https) or S3 URL
      const isAbsoluteUrl = fileUrl.startsWith('http://') || fileUrl.startsWith('https://') || fileUrl.includes('s3');

      // Only add API base if it's a relative path
      if (!isAbsoluteUrl) {
        fileUrl = fileUrl.replace(/^\/+/, '');
        fileUrl = `${process.env.REACT_APP_API_URL}/${fileUrl}`;
      }

      console.log("Final download URL:", fileUrl);

      // Fetch the file as a blob
      // Note: For S3 URLs, we typically don't need credentials header
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Accept': '*/*'
        }
      };

      // Only add credentials for non-S3 URLs
      if (!fileUrl.includes('s3')) {
        fetchOptions.credentials = 'include';
      }

      fetch(fileUrl, fetchOptions)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Create a blob URL instead of using the direct file URL
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = attachment.file_name || "attachment";
          link.style.display = "none";
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);

          console.log("Download completed for:", attachment.file_name);
          setSnackbarMessage("✓ Download completed: " + attachment.file_name);
          setSnackbarStatus(true);
          setShowSnackbar(true);
        })
        .catch(error => {
          console.error("Download error:", error);
          setSnackbarMessage("⚠ Download failed: " + error.message);
          setSnackbarStatus(false);
          setShowSnackbar(true);
        });
    } catch (error) {
      console.error("Download setup error:", error);
      setSnackbarMessage("⚠ Download failed: " + error.message);
      setSnackbarStatus(false);
      setShowSnackbar(true);
    }
  };

  const handleDeleteAttachment = (attachmentId, fileName) => {
    if (!attachmentId || !isValidId(String(attachmentId))) {
      console.error("Invalid attachment ID");
      return;
    }

    setAttachmentToDelete({ id: attachmentId, name: fileName });
    setAttachmentDeleteModal(true);
  };

  const confirmDeleteAttachment = async () => {
    try {
      setIsDeleting(true);
      const response = await removeOrderAttachmentApi(attachmentToDelete.id);

      if (response?.data?.success) {
        setAttachments((prev) =>
          prev.filter((att) => att.id !== attachmentToDelete.id)
        );
        setSnackbarMessage(`✓ Attachment "${attachmentToDelete.name}" deleted successfully`);
        setSnackbarStatus(true);
        setShowSnackbar(true);
        setAttachmentDeleteModal(false);
        setAttachmentToDelete(null);
      } else {
        setSnackbarMessage(`⚠ Failed to delete attachment. Please try again.`);
        setSnackbarStatus(false);
        setShowSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage(`⚠ Error deleting attachment: ${error.message}`);
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteAttachment = () => {
    setAttachmentDeleteModal(false);
    setAttachmentToDelete(null);
  };

  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);

  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };

  const formatAddress = (data = {}) => {
    const { address, city, state, country, zipcode } = data;

    /*  const addr = address;
    if (addr && typeof addr === "string") return addr;
 */
    return [address, city?.label, state?.label, country?.label, zipcode]
      .filter((p) => p && p !== "null")
      .join(", ");
  };

  const getSafeProfileUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    try {
      const parsed = new URL(url);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return parsed.href;
      }
      return "";
    } catch (err) {
      return "";
    }
  };
  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Currency symbol from the order API (falls back to ₹).
  const currencySymbol = order?.currency_symbol
    ? decodeHtmlEntity(order.currency_symbol)
    : "₹";

  // Organization's own currency — used to detect a mismatch against the
  // order/customer currency below.
  const orgCurrencySymbol = decodeHtmlEntity(order?.org_currency_symbol) || "";
  const orgCurrencyName = order?.org_currency_name || "";

  const money = (n) =>
    `${currencySymbol}${toNumber(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Pre-tax subtotal from line items (quantity × unit price).
  const preTaxSubTotal = productList.reduce(
    (sum, item) => sum + toNumber(item.quantity) * toNumber(item.unit_price),
    0,
  );

  // Resolve the total discount amount (Rs. directly, or % of the pre-tax
  // subtotal), then split equally across line items — same as quotation.
  const resolvedDiscount =
    order?.discount_type === "Percentage"
      ? (preTaxSubTotal * toNumber(order?.discount_percentage)) / 100
      : toNumber(order?.discount_amount);
  const hasDiscount = resolvedDiscount > 0;
  const perItemDiscount =
    hasDiscount && productList.length > 0
      ? resolvedDiscount / productList.length
      : 0;

  // Break a line item into parts: base, per-item discount, taxable amount,
  // per-tax amounts, and the discounted+taxed total. Tax is charged on the
  // post-discount amount.
  const getItemBreakdown = (item) => {
    const baseAmount = toNumber(item.quantity) * toNumber(item.unit_price);
    const taxableAmount = Math.max(baseAmount - perItemDiscount, 0);
    const cgst = toNumber(item.cgst_percentage);
    const sgst = toNumber(item.sgst_percentage);
    const igst = toNumber(item.igst_percentage);
    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;
    return {
      baseAmount,
      itemDiscount: perItemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  // Aggregate GST across all items (on the post-discount base). `taxableBase`
  // is the sum of taxable amounts of items that actually carry a GST rate — so
  // the tooltip's "On amount" reconciles with the tax shown.
  const gstTotals = productList.reduce(
    (acc, item) => {
      const bd = getItemBreakdown(item);
      acc.cgst += bd.cgstAmount;
      acc.sgst += bd.sgstAmount;
      acc.igst += bd.igstAmount;
      if (bd.cgst > 0 || bd.sgst > 0 || bd.igst > 0) {
        acc.taxableBase += bd.taxableAmount;
      }
      return acc;
    },
    { cgst: 0, sgst: 0, igst: 0, taxableBase: 0 },
  );
  const totalGst = gstTotals.cgst + gstTotals.sgst + gstTotals.igst;

  const SubTotals = ({ data = {} }) => {
    const calculatedSubTotal = data?.items?.reduce((sum, item) => {
      return sum + (toNumber(item.total_amount) || 0);
    }, 0);

    let discountAmount = 0;
    let discountType = "";
    if (data.discount_type === "Percentage") {
      discountType = "%";
      const discountValue = Number(data.discount_percentage ?? 0);
      discountAmount = discountValue;
    } else {
      discountType = "Rs.";
      const discountValue = Number(data.discount_amount ?? 0);
      discountAmount = discountValue;
    }

    const tdsPercentage = Number(data.tds_percentage ?? 0);
    const tcsPercentage = Number(data.tcs_percentage ?? 0);
    const isTcs = tcsPercentage > 0;
    const tdsTaxAmount = toNumber(isTcs ? data.tcs_amount : data.tds_amount);
    const tdsType = isTcs ? "TCS" : "TDS";
    const tdsRate = isTcs ? tcsPercentage : tdsPercentage;

    const adjustments = toNumber(data.adjustment_amount);
    const total = data.total_amount ?? 0;

    // Base that discount is calculated on. TDS/TCS is calculated from the
    // Total Amount (see below), matching order-create.jsx's formula.
    const onDiscount = money(preTaxSubTotal);

    return (
      <div className="sub-totals">
        <div className="d-flex justify-content-between">
          <div className="sub-title">Sub Total</div>
          <div className="order-sub-title-value">{currencySymbol} {data.sub_total}</div>
        </div>
        <hr />

        {/* Discount */}
        <div className="d-flex justify-content-between">
          <div className="sub-discount-label d-inline-flex align-items-center gap-1">
            Discount ({discountType})
            {hasDiscount && (
              <InfoTooltip
                rows={[
                  ["On amount", onDiscount],
                  [
                    "Entered",
                    data.discount_type === "Percentage"
                      ? `${discountAmount}%`
                      : money(discountAmount),
                  ],
                  ["Discount amount", `- ${money(resolvedDiscount)}`],
                  ...(productList.length > 0
                    ? [[`Per item (÷${productList.length})`, `- ${money(perItemDiscount)}`]]
                    : []),
                ]}
              />
            )}
          </div>
          <div className="sub-title">- {money(resolvedDiscount)}</div>
        </div>

        {/* GST */}
        {totalGst > 0 && (
          <div className="d-flex justify-content-between">
            <div className="sub-discount-label d-inline-flex align-items-center gap-1">
              GST
              <InfoTooltip
                rows={[
                  ["On amount", money(gstTotals.taxableBase)],
                  ...(gstTotals.cgst > 0 ? [["CGST", money(gstTotals.cgst)]] : []),
                  ...(gstTotals.sgst > 0 ? [["SGST", money(gstTotals.sgst)]] : []),
                  ...(gstTotals.igst > 0 ? [["IGST", money(gstTotals.igst)]] : []),
                  ["Total GST", money(totalGst), true],
                ]}
              />
            </div>
            <div className="sub-title">{money(totalGst)}</div>
          </div>
        )}

        {/* Adjustments */}
        <div className="d-flex justify-content-between">
          <div className="sub-discount-label d-inline-flex align-items-center gap-1">
            Adjustments
            <InfoTooltip
              rows={[
                [
                  "Adjustment",
                  `${adjustments < 0 ? "- " : ""}${money(Math.abs(adjustments))}`,
                ],
                ["Applied to", "Grand total"],
              ]}
            />
          </div>
          <div className="sub-title">
            {adjustments < 0 ? "- " : ""}
            {money(Math.abs(adjustments))}
          </div>
        </div>

        <hr />
        <div className="d-flex justify-content-between fw-700">
          <div className="sub-title">Total Amount</div>
          <div className="order-sub-title-value">{currencySymbol} {total}</div>
        </div>

        {/* Organization currency differs from the order/customer currency —
            show the base-currency equivalent (Total Amount × Exchange Rate). */}
        {data.organization_currency_id &&
          data.currency_id &&
          data.organization_currency_id !== data.currency_id && (
            <div className="d-flex justify-content-between mt-2">
              <div className="sub-discount-label d-inline-flex align-items-center gap-1">
                Total in {orgCurrencyName || "Org Currency"}
                <InfoTooltip
                  rows={[
                    ["Total Amount", money(total)],
                    ["Exchange Rate", `× ${toNumber(data.exchange_rate) || 1}`],
                    [
                      "Converted Amount",
                      `${orgCurrencySymbol} ${(toNumber(total) * (toNumber(data.exchange_rate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      true,
                    ],
                  ]}
                />
              </div>
              <div className="sub-title">
                {orgCurrencySymbol} {(toNumber(total) * (toNumber(data.exchange_rate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}

        {/* TDS / TCS — shown below the Total, not included in it */}
        {tdsTaxAmount > 0 && (
          <div className="d-flex justify-content-between mt-2">
            <div className="sub-discount-label d-inline-flex align-items-center gap-1">
              {tdsType} ({tdsRate}%)
              <InfoTooltip
                rows={[
                  ["On amount", money(total)],
                  ["Rate", `${tdsRate}%`],
                  [
                    tdsType,
                    `${isTcs ? "+ " : "- "}${money(tdsTaxAmount)}`,
                    true,
                  ],
                ]}
              />
            </div>
            <div className="sub-title">
              {isTcs ? "+ " : "- "}
              {money(tdsTaxAmount)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="page-container order-view-page-container"
      style={{ backgroundColor: "#F7F8FC", height: "100vh", overflow: "hidden" }}
    >
      <ToastContainer position="top-end">
        <Toast
          bg={snackbarStatus ? "success" : "danger"}
          onClose={() => setShowSnackbar(false)}
          show={showSnackbar}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#fff" }}>{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row
        className="align-items-center justify-content-between"
        style={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #E0E0E0",
          minHeight: "84px",
          padding: "10px 0",
        }}
      >
        <Col sm={12} lg={4}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginLeft: 16,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                border: "1px solid #D4DDE7",
              }}
            >
              <img
                src={Back}
                alt="Back"
                style={{ width: 40, cursor: "pointer" }}
                onClick={() => navigate(-1)}
              />
            </div>

            <div
              style={{
                width: 60,
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <img
                src={getSafeProfileUrl(order?.customer_profile) || ProfileIc}
                alt="Profile"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            </div>

            <div>
              <div className="fs-20 fw-500 text-dkblue">
                {order?.customer_name ?? order?.customer ?? "--"}{" "}
              </div>
              <div className="mt-1">
                <span className="fs-14 fw-500 text-grey">Order No: </span>
                <span className="fs-14 fw-500 textblue">
                  {order?.order_no ?? order?.order_id ?? "--"}
                </span>
                {order?.quotation_no && (
                  <>
                    <span className="fs-14 fw-500 text-grey ms-3">Quotation No: </span>
                    <span className="fs-14 fw-500 textblue" style={{cursor: "pointer"}} onClick={() => navigate("/quotation/view", { state: { id: order?.quotation_id } })}>
                      {order?.quotation_no}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col sm={12} lg={8} className="d-flex justify-content-end align-items-center gap-2">
          {order?.pdf_url && (
            <div
              className="d-flex align-items-center pe-3"
              style={{ cursor: "pointer" }}
              onClick={() => setIsOrderView((prev) => !prev)}
            >
              <EyeIc />
              <span
                className="fs-13 fw-400 textblue ms-1"
                style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                {isOrderView ? "Back" : "Pdf view"}
              </span>
            </div>
          )}
          {!isOrderView &&
            hasEditPermission &&
            (statusTransitions[order?.status] || []).map((nextStatus) => (
              <Button
                key={nextStatus}
                className={`cursor-pointer fs-14 fw-700 ${
                  loadingStatus ? "disabled-btn" : ""
                } border-0 rounded-2 py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={
                  !loadingStatus
                    ? () => {
                        updateStatus(nextStatus);
                      }
                    : () => {}
                }
                style={{
                  minWidth: "100px",
                  background: nextStatus === "Cancelled" ? "#FF4444" : "#4EBD7E",
                }}
              >
                {loadingStatus === nextStatus ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  nextStatus
                )}
              </Button>
            ))}
          {order?.status === "Created" && hasEditPermission && !isOrderView && (
            <div
              className="fs-14 fw-600 text-dkblue d-flex justify-content-center align-items-center"
              onClick={() => navigate("/order/create", { state: { id } })}
              style={{
                cursor: "pointer",
                width: "82px",
                height: "36px",
                borderRadius: "100px",
                border: "1px solid #D4DDE7",
              }}
              title="Edit Order"
            >
              Edit
            </div>
          )}
          {order?.status === "Created" && hasDeletePermission && !isOrderView && (
            <div
              className="fs-14 fw-600 text-dkblue d-flex justify-content-center align-items-center"
              onClick={() => {
                // Implement delete logic if needed
              }}
              style={{
                cursor: "pointer",
                width: "36px",
                height: "36px",
                borderRadius: "100px",
                border: "1px solid #D4DDE7",
              }}
              title="Delete Order"
            >
              <DeleteRow />
            </div>
          )}
        </Col>
      </Row>

      {/* PDF view — replaces the order details while active */}
      {isOrderView && (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "calc(95vh - 84px)",
          }}
        >
          {/* Close Icon */}
          <div
            onClick={() => setIsOrderView(false)}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              zIndex: 9999,
              background: "#fff",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            ✕
          </div>

          <iframe
            src={order?.pdf_url ?? ""}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="Order PDF Viewer"
          />
        </div>
      )}

      {/* Tabs under header */}

      {!isOrderView && (
      <div className="d-flex" style={{ height: "calc(95vh - 84px)" }}>
        <div className="col-12 col-md-4">
          <div className="order-card-left p-3">
            <h5 className="card-title">Account details</h5>

            <div className="account-details">
              <div className="account-row">
                <img src={Phone} alt="phone" className="account-icon" />
                <div>
                  <div className="fs-13 text-grey">
                    {order?.phonenumber ?? "--"}
                  </div>
                </div>
              </div>

              <div className="account-row mt-2">
                <img src={Mail} alt="mail" className="account-icon" />
                <div>
                  <a
                    className="fs-14 textblue"
                    href={`mailto:${order?.email ?? ""}`}
                  >
                    {order?.email ?? "--"}
                  </a>
                </div>
              </div>

              <div className="account-row mt-3 align-items-start">
                <img src={User} alt="user" className="account-icon" />
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="fs-14 fw-600">
                      {order?.owner_name ?? "--"}
                    </div>
                    {order?.owner_name && order.owner_name.trim() !== "" && order.owner_name.trim() !== "--" && (
                      <div className="owner-badge">Account owner</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="account-row mt-3">
                <img
                  src={AccountType}
                  alt="services"
                  className="account-icon"
                />
                <div>
                  <div className="fs-14">
                    {order?.customer_industry ?? "--"}
                  </div>
                </div>
              </div>

              <div className="account-row mt-3">
                <img src={Amount} alt="amount" className="account-icon" />
                <div>
                  <div className="fs-14">
                    {order?.credit_amount ? `${currencySymbol} ${order.credit_amount}` : "--"}
                  </div>
                </div>
              </div>

              <div className="account-row mt-3">
                <img src={Location} alt="location" className="account-icon" />
                <div>
                  <div className="fs-14">
                    {order?.customer_location ?? "--"}
                  </div>
                </div>
              </div>

              <hr />

              <div className="fs-14 fw-600 text-dkblue">Sales Person</div>
              <div className="sales-person mt-2">
                <div className="d-flex gap-3 align-items-center">
                  <img
                    src={getSafeProfileUrl(order?.sales_person_profile) || getSafeProfileUrl(order?.sales_person_profilephoto) || getSafeProfileUrl(order?.employee_profile) || ProfileIc}
                    alt="sales"
                    className="sales-avatar"
                  />
                  <div>
                    <div className="fw-600 text-dkblue fs-15">
                      {order?.sales_person_name ?? order?.sales_person ?? "--"}
                    </div>
                    <div className="d-flex align-items-center gap-2 fs-13 text-muted mt-1">
                      <img src={Phone} alt="phone" style={{ width: "16px", height: "16px", opacity: 0.6 }} />
                      <span>{order?.sales_person_phone ?? "--"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 fs-13 textblue mt-1">
                      <img src={Mail} alt="mail" style={{ width: "16px", height: "16px", opacity: 0.6 }} />
                      <a href={`mailto:${order?.sales_person_email ?? ""}`} className="textblue" style={{ textDecoration: "none" }}>
                        {order?.sales_person_email ?? ""}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              <div className="mt-3">
                <div className="fs-14 fw-600 text-dkblue">Created at</div>
                <div className="fs-14 text-muted mt-1">
                  {order?.created_at_formatted ? order.created_at_formatted : (order?.date ?? "--")} by
                </div>
                <div className="fs-14 fw-600 text-dkblue mt-1">
                  {order?.created_by ?? "--"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-8 order-card-right">
          <div className="order-tabs-container">
            <div
              className={`order-tab ${activeTab === "Order Details" ? "active" : ""}`}
              onClick={() => setActiveTab("Order Details")}
            >
              Order Details
            </div>
            <div
              className={`order-tab ${activeTab === "Invoice" ? "active" : ""}`}
              onClick={() => setActiveTab("Invoice")}
            >
              Invoice
            </div>
            <div
              className={`order-tab ${activeTab === "Workflow" ? "active" : ""}`}
              onClick={() => setActiveTab("Workflow")}
            >
              Workflow
            </div>
            <div
              className={`order-tab ${activeTab === "Log" ? "active" : ""}`}
              onClick={() => setActiveTab("Log")}
            >
              Log
            </div>
            <div
              className={`order-tab ${activeTab === "Attachments" ? "active" : ""}`}
              onClick={() => setActiveTab("Attachments")}
            >
              Attachments
            </div>
          </div>

          {activeTab === "Order Details" && (
            <div className="custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(95vh - 160px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
              <div className="mt-3 order-card p-4 order-details-card">
                <div className="card-title">Order details</div>
                {isLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Loader height="150px" />
                  </div>
                ) : (
                  <div className="details-grid">
                    <div className="details-row">
                      <div className="details-label">Date</div>
                      <div className="details-value">
                        {order?.date ?? order?.order_date ?? "--"}
                      </div>
                    </div>

                    <div className="details-row">
                      <div className="details-label">Currency</div>
                      <div className="details-value">
                        <a className="textblue">
                          {order?.currency ?? "--"}
                        </a>
                      </div>
                    </div>

                    {order?.organization_currency_id &&
                      order?.currency_id &&
                      order.organization_currency_id !== order.currency_id &&
                      order?.exchange_rate != null &&
                      order?.exchange_rate !== "" && (
                        <div className="details-row">
                          <div className="details-label">Exchange Rate</div>
                          <div className="details-value">
                            {order.exchange_rate}
                          </div>
                        </div>
                      )}

                    <div className="details-row">
                      <div className="details-label">Order type</div>
                      <div className="details-value">
                        {order?.order_type ?? "--"}
                      </div>
                    </div>

                    {/* <div className="details-row">
                        <div className="details-label">Payment type</div>
                        <div className="details-value">
                          {order?.payment_type ?? "--"}
                        </div>
                      </div>

                      <div className="details-row">
                        <div className="details-label">Payment status</div>
                        <div className="details-value">
                          {order?.payment_status ? (
                            <span className="status-badge">
                              <span className="status-dot" />{" "}
                              {order.payment_status}
                            </span>
                          ) : (
                            "--"
                          )}
                        </div>
                      </div> */}

                    <div className="details-row">
                      <div className="details-label">Title</div>
                      <div className="details-value">
                        {order?.title ?? "--"}
                      </div>
                    </div>

                    <div className="details-row">
                      <div className="details-label">Description</div>
                      <div className="details-value desc">
                        <p>{order?.description ?? "--"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="order-card p-3 mt-3 address-wrapper"
                style={{ position: "relative" }}
              >
                <div className="card-title">Address Information</div>
                {/* <a className="locate-link">Locate in Map</a> */}
                <div className="d-flex gap-4 mt-3">
                  <div style={{ flex: 1 }} className="address-card">
                    <div className="d-flex align-items-start gap-3">
                      <div className="addr-icon">
                        <img src={Location} alt="loc" />
                      </div>
                      <div>
                        <div className="address-header">BILLING ADDRESS</div>
                        <div className="fs-14">
                          {formatAddress(order?.bill_address ?? {})}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ flex: 1 }} className="address-card">
                    <div className="d-flex align-items-start gap-3">
                      <div className="addr-icon">
                        <img src={Location} alt="loc" />
                      </div>
                      <div>
                        <div className="address-header">SHIPPING ADDRESS</div>
                        <div className="fs-14">
                          {formatAddress(order?.ship_address ?? {})}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products + Totals layout */}
              <div className="mt-3">
                <div className="order-card p-3">
                  <h5 className="card-title">Products</h5>
                  <div className="items-table-wrapper">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>HSN</th>
                          <th>DESCRIPTION</th>
                          <th>UNIT</th>
                          <th>QUANTITY</th>
                          <th>INVOICED QTY</th>
                          <th>UNIT PRICE</th>
                          <th>AMOUNT</th>
                          {hasDiscount && <th>DISCOUNT</th>}
                          {isCgst && <th>CGST %</th>}
                          {isSgst && <th>SGST %</th>}
                          {isIgst && <th>IGST %</th>}
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productList && productList.length > 0 ? (
                          productList.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.hsn_no ?? item.product?.hsn_no ?? "--"}</td>
                              <td>
                                {item.product ?? item.service_name ?? "--"}
                              </td>
                              <td>{item.uom ?? "--"}</td>
                              <td>{item.quantity ?? "--"}</td>
                              <td>{Number(item.invoiced_quantity) > 0 ? item.invoiced_quantity : "--"}</td>
                              <td>{currencySymbol} {item.unit_price ?? "0"}</td>
                              <td>{money(getItemBreakdown(item).baseAmount)}</td>
                              {hasDiscount && (
                                <td>{money(getItemBreakdown(item).itemDiscount)}</td>
                              )}
                              {isCgst && (
                                <td>
                                  {currencySymbol} {item.cgst_amount ?? "0"}(
                                  {item.cgst_percentage ?? "--"}%)
                                </td>
                              )}
                              {isSgst && (
                                <td>
                                  {currencySymbol} {item.sgst_amount ?? "0"} ({" "}
                                  {item.sgst_percentage ?? "--"}%)
                                </td>
                              )}

                              {isIgst && (
                                <td>
                                  {currencySymbol} {item.igst_amount ?? "0"} ({" "}
                                  {item.igst_percentage ?? "--"}%)
                                </td>
                              )}

                              <td>
                                {(() => {
                                  const bd = getItemBreakdown(item);
                                  const rows = [
                                    ["Amount", money(bd.baseAmount)],
                                    ...(bd.itemDiscount > 0
                                      ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                                      : []),
                                    ["Taxable", money(bd.taxableAmount)],
                                    ...(bd.cgst > 0
                                      ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                                      : []),
                                    ...(bd.sgst > 0
                                      ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                                      : []),
                                    ...(bd.igst > 0
                                      ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                                      : []),
                                    ["Total", money(bd.total), true],
                                  ];
                                  return (
                                    <span className="d-inline-flex align-items-center gap-1">
                                      {money(bd.total)}
                                      <InfoTooltip rows={rows} />
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={10} className="text-center">
                              No items
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex gap-3 mt-2">
                    <div style={{ flex: 1 }}>
                      <div className="mt-3">
                        <div className="section-subtitle">
                          Terms & Conditions
                        </div>
                        <div className="fs-13 text-muted">
                          {order?.terms_condition ?? ""}
                        </div>

                        <div className="section-subtitle mt-3">
                          Customer Notes
                        </div>
                        <div className="fs-13 text-muted">
                          {order?.customer_note ?? ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="order-card p-3 totals-card-inline">
                        <SubTotals data={order ?? {}} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "Invoice" && (
            <div className="custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(95vh - 160px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
              <div className="mt-3 order-card p-4 order-details-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="card-title mb-0">Invoices - {invoiceList.length}</div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "100px",
                      border: "0.8px solid #e5e5e5",
                      padding: "8px 14px",
                      backgroundColor: "#ffffff",
                      height: "36px",
                    }}
                  >
                    <img src={Search} alt="search" style={{ width: "14px" }} />
                    <input
                      type="search"
                      placeholder="Search invoices..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchInvoices(invoiceSearch);
                      }}
                      className="no-focus-border"
                      style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent" }}
                    />
                  </div>
                </div>

                {isInvoiceLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Loader height="150px" />
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      <div className="order-card p-3" style={{ flex: 1, minWidth: "160px" }}>
                        <div className="fs-12 text-muted">Total Invoiced</div>
                        <div className="fs-16 fw-600 text-dkblue">
                          {currencySymbol} {invoiceSummary.total_invoiced}
                        </div>
                      </div>
                      <div className="order-card p-3" style={{ flex: 1, minWidth: "160px" }}>
                        <div className="fs-12 text-muted">Total Paid</div>
                        <div className="fs-16 fw-600" style={{ color: "#1aa56a" }}>
                          {currencySymbol} {invoiceSummary.total_paid}
                        </div>
                      </div>
                      <div className="order-card p-3" style={{ flex: 1, minWidth: "160px" }}>
                        <div className="fs-12 text-muted">Outstanding</div>
                        <div className="fs-16 fw-600" style={{ color: "#e53935" }}>
                          {currencySymbol} {invoiceSummary.total_outstanding}
                        </div>
                      </div>
                    </div>

                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th style={{ width: "40px" }}>S.No</th>
                          <th>Invoice No.</th>
                          <th>Invoice Date</th>
                          <th>Due Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceList.length === 0 && (
                          <tr>
                            <td colSpan="6" style={{ textAlign: "center", color: "#8790A1" }}>
                              {invoiceSearch ? "No invoices match your search." : "No invoices raised for this order yet."}
                            </td>
                          </tr>
                        )}
                        {invoiceList.map((invoice, index) => (
                          <tr
                            key={invoice.id}
                            style={{ cursor: hasInvoiceViewPermission ? "pointer" : "not-allowed" }}
                            onClick={() => {
                              if (hasInvoiceViewPermission) {
                                navigate("/invoice/view", { state: { id: invoice.id } });
                              }
                            }}
                          >
                            <td>{index + 1}</td>
                            <td style={{ color: "#3D4AF5", fontWeight: "500" }}>{invoice.invoice_no}</td>
                            <td>{invoice.invoice_date}</td>
                            <td>{invoice.due_date}</td>
                            <td>{currencySymbol} {invoice.amount}</td>
                            <td>
                              <span className={`status-pill ${invoice.status?.toLowerCase()}`}>{invoice.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "Workflow" &&
            (() => {
              let formattedDate = "";
              if (workflowList.length > 0 && workflowList[0].date) {
                const dateObj = new Date(workflowList[0].date);
                formattedDate = dateObj.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                });
              }
              return (
                <div className="mt-3">
                  <div className="order-card p-4">
                    <div className="card-title mb-1">Workflow</div>
                    {isLoading ? (
                      <div className="d-flex justify-content-center py-4">
                        <Loader height="150px" />
                      </div>
                    ) : (
                      <div className="py-4 custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(100vh - 220px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
                        <div>
                          <div style={{ minWidth: "300px" }}>
                            {formattedDate && (
                              <div className="position-relative text-center mb-4">
                                <hr />
                                <span
                                  className="position-absolute top-50 start-50 translate-middle bg-white px-4 py-1 text-muted small border rounded-pill"
                                  style={{ fontWeight: 500, fontSize: "15px" }}
                                >
                                  {formattedDate}
                                </span>
                              </div>
                            )}

                            {workflowList && workflowList.length > 0 ? (
                              workflowList.map((workflowItem, index) => {
                                const statusKey =
                                  workflowItem.status?.toLowerCase() || "";
                                const Icon =
                                  workflowIcons[statusKey] ||
                                  workflowIcons["pending"];

                                return (
                                  <div
                                    key={index}
                                    className="d-flex align-items-stretch"
                                    style={{ marginBottom: "24px" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "start",
                                        marginRight: "1rem",
                                        alignSelf: "stretch"
                                      }}
                                    >
                                      <div
                                        className="small text-muted mb-1"
                                        style={{
                                          marginRight: "1rem",
                                          minWidth: "60px",
                                        }}
                                      >
                                        {workflowItem.time}
                                      </div>

                                      <div
                                        style={{
                                          position: "relative",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          alignSelf: "stretch"
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 1,
                                          }}
                                        >
                                          {Icon}
                                        </div>

                                        {index !== workflowList.length - 1 && (
                                          <div
                                            style={{
                                              flex: 1,
                                              width: "0px",
                                              borderLeft: "1.5px dashed #D3D8E0",
                                              marginTop: "4px",
                                              marginBottom: "-24px"
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                      <p className="mb-1">
                                        <strong
                                          style={{
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            color: "#21272C",
                                          }}
                                        >
                                          {workflowItem.status}
                                        </strong>
                                      </p>
                                      <div className="small text-muted">
                                        By{" "}
                                        {workflowItem.first_name ||
                                          workflowItem.updated_by ||
                                          "System"}{" "}
                                        on {workflowItem.date}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "40px",
                                  color: "#999",
                                  fontSize: "14px",
                                }}
                              >
                                "No workflow found"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {activeTab === "Log" &&
            (() => {
              let formattedDate = "";
              if (historyList.length > 0 && historyList[0].date) {
                const dateObj = new Date(historyList[0].date);
                formattedDate = dateObj.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                });
              }
              return (
                <div className="mt-3">
                  <div className="order-card p-4">
                    <div className="card-title mb-1">Log</div>
                    {isLoading ? (
                      <div className="d-flex justify-content-center py-4">
                        <Loader height="150px" />
                      </div>
                    ) : (
                      <div className="py-4 custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(100vh - 220px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
                        <div>
                          <div style={{ minWidth: "300px" }}>
                            {formattedDate && (
                              <div className="position-relative text-center mb-4">
                                <hr />
                                <span
                                  className="position-absolute top-50 start-50 translate-middle bg-white px-4 py-1 text-muted small border rounded-pill"
                                  style={{ fontWeight: 500, fontSize: "15px" }}
                                >
                                  {formattedDate}
                                </span>
                              </div>
                            )}

                            {historyList && historyList.length > 0 ? (
                              historyList.map((HistoryItem, index) => {
                                const iconKey = getCleanType(HistoryItem.type);
                                const Icon =
                                  historyIcons[iconKey] || historyIcons["note"];

                                return (
                                  <div
                                    key={index}
                                    className="d-flex align-items-stretch"
                                    style={{ marginBottom: "24px" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "start",
                                        marginRight: "1rem",
                                        alignSelf: "stretch"
                                      }}
                                    >
                                      <div
                                        className="small text-muted mb-1"
                                        style={{
                                          marginRight: "1rem",
                                          minWidth: "60px",
                                        }}
                                      >
                                        {HistoryItem.time}
                                      </div>

                                      <div
                                        style={{
                                          position: "relative",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          alignSelf: "stretch"
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 1,
                                          }}
                                        >
                                          {Icon}
                                        </div>

                                        {index !== historyList.length - 1 && (
                                          <div
                                            style={{
                                              flex: 1,
                                              width: "0px",
                                              borderLeft: "1.5px dashed #D3D8E0",
                                              marginTop: "4px",
                                              marginBottom: "-24px"
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                      <p className="mb-1" style={{ fontSize: "14px" }}>
                                        <strong
                                          style={{
                                            fontWeight: "600",
                                            fontSize: "15px",
                                            color: "#172B4D",
                                          }}
                                        >
                                          {HistoryItem.type}
                                        </strong>{" "}
                                        <span style={{ color: "#8790A1", fontSize: "13px" }}>
                                          ( by {HistoryItem.first_name || "System"} )
                                        </span>
                                      </p>
                                      <div className="small text-muted mb-2">
                                        {HistoryItem.date}
                                      </div>

                                      {/* Boxed card of changes */}
                                      {HistoryItem.changes && HistoryItem.changes.length > 0 && (
                                        <div
                                          style={{
                                            background: "#FFFFFF",
                                            borderRadius: "12px",
                                            padding: "16px",
                                            border: "1px solid #E5E8EB",
                                            marginTop: "8px",
                                            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.02)",
                                            maxWidth: "600px"
                                          }}
                                        >
                                          {HistoryItem.changes.map((change, cIdx) => (
                                            <div key={cIdx} className={cIdx > 0 ? "mt-3" : ""}>
                                              {change.field ? (
                                                <>
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      fontWeight: "600",
                                                      color: "#8790A1",
                                                      textTransform: "capitalize",
                                                      marginBottom: "2px"
                                                    }}
                                                  >
                                                    {change.field}
                                                  </div>
                                                  <div style={{ fontSize: "13px", color: "#172B4D" }}>
                                                    {change.old}{" "}
                                                    <span style={{ color: "#5D5FEF", fontWeight: "600", margin: "0 4px" }}>
                                                      →
                                                    </span>{" "}
                                                    {change.new}
                                                  </div>
                                                </>
                                              ) : (
                                                <div style={{ fontSize: "13px", color: "#172B4D" }}>
                                                  {change.new}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "40px",
                                  color: "#999",
                                  fontSize: "14px",
                                }}
                              >
                                No history found
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {activeTab === "Attachments" && (
            <div className="mt-3">
              <div className="order-card p-4">
                <div className="card-title mb-4">Attachments</div>
                {isLoading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Loader height="150px" />
                  </div>
                ) : (
                  <div className="py-4 custom-scrollbar" style={{ overflowX: "hidden", maxHeight: "calc(100vh - 220px)", overflowY: "auto", paddingRight: "8px", paddingBottom: "24px" }}>
                    {attachments && attachments.length > 0 ? (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: "20px",
                      }}>
                        {attachments.map((attachment, index) => (
                          <div
                            key={index}
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: "12px",
                              padding: "16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                              backgroundColor: "#f9fafb",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = "none";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}>
                              <div style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "8px",
                                backgroundColor: "#10b981",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: "700",
                                flexShrink: 0,
                              }}>
                                {attachment.file_name.split(".").pop().toUpperCase()}
                              </div>
                              <div style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}>
                                <div style={{
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: "#1f2937",
                                  wordBreak: "break-word",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}>
                                  {attachment.file_name}
                                </div>
                                <div style={{
                                  fontSize: "12px",
                                  color: "#9ca3af",
                                }}>
                                  {attachment.file_size ? `${attachment.file_size} MB` : "Size unknown"}
                                </div>
                              </div>
                            </div>

                            <div style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "flex-end",
                            }}>
                              {isPreviewableFile(attachment.file_name) && (
                                <button
                                  onClick={() => handleOpenPreview(attachment)}
                                  title="Preview"
                                  style={{
                                    padding: "8px 12px",
                                    backgroundColor: "#f3f4f6",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontSize: "12px",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#e5e7eb";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                                  }}
                                >
                                  <FaEye size={14} style={{ color: "#3b82f6" }} />
                                  Preview
                                </button>
                              )}

                              <button
                                onClick={() => handleDownloadAttachment(attachment)}
                                title="Download"
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#dbeafe",
                                  border: "1px solid #93c5fd",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "12px",
                                  textDecoration: "none",
                                  color: "#1e40af",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#bfdbfe";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#dbeafe";
                                }}
                              >
                                <FaDownload size={14} />
                                Download
                              </button>

                              <button
                                onClick={() => handleDeleteAttachment(attachment.id, attachment.file_name)}
                                title="Delete"
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#fee2e2",
                                  border: "1px solid #fecaca",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fca5a5";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fee2e2";
                                }}
                              >
                                <FaTrash size={14} style={{ color: "#dc2626" }} />
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: "#9ca3af",
                        fontSize: "14px",
                      }}>
                        <div style={{ marginBottom: "8px" }}>📎 No attachments yet</div>
                        <div style={{ fontSize: "12px", color: "#d1d5db" }}>
                          Attachments will appear here
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preview Modal - Image */}
          {previewModal && previewData && isImageFile(previewData.file_name) && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10001,
              }}
              onClick={() => setPreviewModal(false)}
            >
              <div
                style={{
                  maxWidth: "90%",
                  maxHeight: "90%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: "16px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>
                    {previewData.file_name}
                  </div>
                  <button
                    onClick={() => setPreviewModal(false)}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    flex: 1,
                    overflow: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={
                      previewData.file_path?.startsWith('http')
                        ? previewData.file_path
                        : `${process.env.REACT_APP_API_URL}/${previewData.file_path || previewData.file_url || ""}`
                    }
                    alt={previewData.file_name}
                    onError={(e) => {
                      console.error("Image failed to load:", e.target.src);
                      e.target.src = ""; // Clear the src to prevent broken image icon
                    }}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Preview Modal - PDF */}
          {previewModal && previewData && isPdfFile(previewData.file_name) && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10001,
              }}
              onClick={() => setPreviewModal(false)}
            >
              <div
                style={{
                  maxWidth: "90%",
                  maxHeight: "90%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: "16px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>
                    {previewData.file_name}
                  </div>
                  <button
                    onClick={() => setPreviewModal(false)}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#6b7280",
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  <iframe
                    src={
                      previewData.file_path?.startsWith('http')
                        ? previewData.file_path
                        : `${process.env.REACT_APP_API_URL}/${previewData.file_path || previewData.file_url || ""}`
                    }
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title="PDF Preview"
                    onError={(e) => {
                      console.error("PDF failed to load:", e.target.src);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Attachment Delete Modal */}
          <AttachmentDeleteModal
            isOpen={attachmentDeleteModal}
            attachment={attachmentToDelete}
            isDeleting={isDeleting}
            onConfirm={confirmDeleteAttachment}
            onCancel={cancelDeleteAttachment}
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default OrderView;
